import React, { useEffect, useState } from "react";
import AdminLayout from "../layout/AdminLayout";
import Header from "../components/Header";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEmailDrafts,
  saveEmailDraft,
  deleteEmailDraft,
  sendEmailDraft,
  clearTrialMessages,
} from "../redux/reducers/trialSlice";
import { getUsers } from "../redux/reducers/userSlice";
import {
  FiSend,
  FiUsers,
  FiUser,
  FiSave,
  FiTrash2,
  FiMail,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";

const EmailCenter = () => {
  const dispatch = useDispatch();
  const { emailLoading, draftLoading, drafts, error, successMessage } = useSelector(
    (state) => state.trial
  );
  const { users } = useSelector((state) => state.users);

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [targetMode, setTargetMode] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState("compose"); // 'compose' | 'drafts'
  const [confirmSend, setConfirmSend] = useState(null); // draft id to confirm

  useEffect(() => {
    dispatch(getUsers());
    dispatch(fetchEmailDrafts());
  }, [dispatch]);

  useEffect(() => {
    if (successMessage || error) {
      const t = setTimeout(() => dispatch(clearTrialMessages()), 5000);
      return () => clearTimeout(t);
    }
  }, [successMessage, error, dispatch]);

  const toggleUser = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSaveDraft = (e) => {
    e.preventDefault();
    if (!subject || !message) return;
    dispatch(
      saveEmailDraft({
        subject,
        message,
        userIds: targetMode === "selected" ? selectedIds : [],
        targetMode,
      })
    ).then((res) => {
      if (!res.error) {
        setSubject("");
        setMessage("");
        setSelectedIds([]);
        setActiveTab("drafts");
      }
    });
  };

  const handleSendDraft = (id) => {
    dispatch(sendEmailDraft(id));
    setConfirmSend(null);
  };

  const handleDeleteDraft = (id) => {
    dispatch(deleteEmailDraft(id));
  };

  const fmt = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  const pendingDrafts = (drafts ?? []).filter((d) => d.status === "draft");
  const sentDrafts = (drafts ?? []).filter((d) => d.status === "sent");

  return (
    <AdminLayout>
      <div className="p-4">
        <Header
          title="Email Center"
          subTitle="Admin"
          description="Compose emails, save as drafts, and send when ready."
        />

        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm">
            ✅ {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b-2 border-gray-100">
          <button
            onClick={() => setActiveTab("compose")}
            className={`pb-3 px-4 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === "compose"
                ? "border-purple-500 text-purple-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Compose
          </button>
          <button
            onClick={() => setActiveTab("drafts")}
            className={`pb-3 px-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "drafts"
                ? "border-purple-500 text-purple-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Drafts
            {pendingDrafts.length > 0 && (
              <span className="bg-purple-100 text-purple-600 text-xs font-bold px-2 py-0.5 rounded-full">
                {pendingDrafts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("sent")}
            className={`pb-3 px-4 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === "sent"
                ? "border-purple-500 text-purple-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Sent ({sentDrafts.length})
          </button>
        </div>

        {/* ── COMPOSE TAB ── */}
        {activeTab === "compose" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <fieldset className="border-2 rounded-2xl px-6 py-6">
                <legend className="font-bold px-1">New Email</legend>

                <div className="flex gap-3 mt-3 mb-5">
                  <button
                    type="button"
                    onClick={() => setTargetMode("all")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border-2 transition-colors ${
                      targetMode === "all"
                        ? "border-purple-500 bg-purple-50 text-purple-700"
                        : "border-gray-200 text-gray-500"
                    }`}
                  >
                    <FiUsers /> All Users ({users?.length ?? 0})
                  </button>
                  <button
                    type="button"
                    onClick={() => setTargetMode("selected")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border-2 transition-colors ${
                      targetMode === "selected"
                        ? "border-purple-500 bg-purple-50 text-purple-700"
                        : "border-gray-200 text-gray-500"
                    }`}
                  >
                    <FiUser /> Selected ({selectedIds.length})
                  </button>
                </div>

                <form onSubmit={handleSaveDraft} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                      placeholder="e.g. Important Update from Revenue Pro Planner"
                      className="w-full border-2 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Message
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      rows={8}
                      placeholder="Write your message here..."
                      className="w-full border-2 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-500 resize-none"
                    />
                    <p className="text-gray-400 text-xs mt-1">
                      Each email is personalized with the user's name automatically.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={draftLoading || !subject || !message}
                      className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-semibold"
                    >
                      <FiSave />
                      {draftLoading ? "Saving..." : "Save as Draft"}
                    </button>
                    <p className="text-gray-400 text-xs self-center">
                      Review and send from the Drafts tab.
                    </p>
                  </div>
                </form>
              </fieldset>
            </div>

            {/* User selection panel */}
            <div>
              <fieldset className="border-2 rounded-2xl px-4 py-5 h-full">
                <legend className="font-bold px-1">
                  {targetMode === "selected" ? "Select Recipients" : "All Recipients"}
                </legend>
                <p className="text-gray-400 text-xs mt-2 mb-3">
                  {targetMode === "selected"
                    ? "Click users to select them"
                    : "All users will receive this email"}
                </p>
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                  {(users ?? []).map((u) => {
                    const isSelected = selectedIds.includes(u._id);
                    return (
                      <div
                        key={u._id}
                        onClick={() => targetMode === "selected" && toggleUser(u._id)}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 text-sm transition-colors ${
                          targetMode === "selected"
                            ? isSelected
                              ? "border-purple-500 bg-purple-50 cursor-pointer"
                              : "border-gray-100 hover:border-gray-300 cursor-pointer"
                            : "border-gray-100 opacity-60"
                        }`}
                      >
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                            isSelected ? "bg-purple-500 text-white" : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {u.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-700 truncate">{u.name}</p>
                          <p className="text-gray-400 text-xs truncate">{u.email}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </fieldset>
            </div>
          </div>
        )}

        {/* ── DRAFTS TAB ── */}
        {activeTab === "drafts" && (
          <div>
            {pendingDrafts.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-2xl">
                <FiMail className="text-gray-300 text-4xl mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No drafts yet. Compose one above.</p>
                <button
                  onClick={() => setActiveTab("compose")}
                  className="mt-4 text-purple-500 text-sm font-medium hover:underline"
                >
                  Go to Compose
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingDrafts.map((draft) => (
                  <div key={draft._id} className="border-2 rounded-2xl p-5 bg-white">
                    {confirmSend === draft._id ? (
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <p className="text-sm font-medium text-gray-700">
                          Send <strong>"{draft.subject}"</strong> to{" "}
                          {draft.targetMode === "all"
                            ? `all users`
                            : `${draft.userIds?.length ?? 0} selected users`}
                          ?
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSendDraft(draft._id)}
                            disabled={emailLoading}
                            className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50"
                          >
                            <FiSend /> {emailLoading ? "Sending..." : "Yes, Send Now"}
                          </button>
                          <button
                            onClick={() => setConfirmSend(null)}
                            className="px-4 py-2 border-2 rounded-xl text-sm text-gray-600 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                              <FiClock className="text-xs" /> Draft
                            </span>
                            <span className="text-xs text-gray-400">
                              {draft.targetMode === "all"
                                ? "All users"
                                : `${draft.userIds?.length ?? 0} selected`}
                            </span>
                          </div>
                          <h3 className="font-semibold text-gray-800 truncate">{draft.subject}</h3>
                          <p className="text-gray-500 text-sm mt-1 line-clamp-2">{draft.message}</p>
                          <p className="text-gray-400 text-xs mt-2">Saved {fmt(draft.createdAt)}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => setConfirmSend(draft._id)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl text-xs font-semibold"
                          >
                            <FiSend /> Send
                          </button>
                          <button
                            onClick={() => handleDeleteDraft(draft._id)}
                            className="text-gray-400 hover:text-red-600 transition-colors p-2"
                            title="Delete draft"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── SENT TAB ── */}
        {activeTab === "sent" && (
          <div>
            {sentDrafts.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-2xl">
                <FiCheckCircle className="text-gray-300 text-4xl mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No sent emails yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sentDrafts.map((draft) => (
                  <div key={draft._id} className="border-2 rounded-2xl p-5 bg-gray-50 opacity-80">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                            <FiCheckCircle className="text-xs" /> Sent
                          </span>
                          <span className="text-xs text-gray-400">
                            {draft.recipientCount} recipients
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-800 truncate">{draft.subject}</h3>
                        <p className="text-gray-500 text-sm mt-1 line-clamp-2">{draft.message}</p>
                        <p className="text-gray-400 text-xs mt-2">Sent {fmt(draft.sentAt)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default EmailCenter;
